import { getIn, useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { INewPartnerCostFormValues } from 'features/PartnerCost/types'
import { PartnerAutocomplete } from 'features/Partners/components/PartnerAutocomplete'
import { Label } from 'components/v2/Label'
import { MultiVehicleAutocomplete } from 'features/Vehicles/components/MultiVehicleAutocomplete'
import { RouteAutocomplete } from 'features/Routes/components/RouteAutocomplete'
import { MultiRouteServiceAutocomplete } from 'features/Services/components/MultiRouteServiceAutocomplete'
import { InputDate } from 'components/v2/InputDate'
import { subMonths } from 'date-fns'
import { DowSelector } from 'components/v2/DowSelector'
import { isEmpty, uniqBy } from 'lodash'
import { PARTNER_COST_VERTICALS } from 'features/PartnerCost/utils'
import { updateDateRangeByEndDate, updateDateRangeByStartDate } from 'utils/date'

const messages = defineMessages({
  partnerPlaceholder: {
    id: 'partnerCost.form.partner.placeholder',
    defaultMessage: 'Select Partner'
  },
  partnerVehiclesPlaceholder: {
    id: 'partnerCost.form.partnerVehicles.placeholder',
    defaultMessage: 'Select partner vehicles'
  },
  routePlaceholder: {
    id: 'partnerCost.form.route.placeholder',
    defaultMessage: 'Select Route'
  },
  routeServicesPlaceholder: {
    id: 'partnerCost.form.routeServices.placeholder',
    defaultMessage: 'Select route services'
  },
  endAtPlaceholder: {
    id: 'partnerCost.form.endDate.placeholder',
    defaultMessage: 'Select end At'
  }
})

export const RuleCostCard = (): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue, setValues, touched, errors } = useFormikContext<INewPartnerCostFormValues>()

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="partnerCost.form.section.ruleCost.title" defaultMessage="Rule Cost" />
      </Heading.H4>
      <Row>
        <Column width="50%">
          <PartnerAutocomplete
            id="partner"
            value={values.partner?.name || ''}
            onChange={(partner): void => {
              setValues({
                ...values,
                partner: partner?.id ? partner : null,
                partnerVehicles: []
              })
            }}
            placeholder={intl.formatMessage(messages.partnerPlaceholder)}
            label={<FormattedMessage id="partnerCost.form.partner.label" defaultMessage="Partner" />}
            optional
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Label>
            <FormattedMessage id="partnerCost.form.vehiclePartner.label" defaultMessage="Vehicle Partner id" />
          </Label>
          <MultiVehicleAutocomplete
            id="partnerVehicles"
            placeholder={intl.formatMessage(messages.partnerVehiclesPlaceholder)}
            values={values.partnerVehicles}
            onChange={(vehicles): void => {
              setValues({
                ...values,
                partnerVehicles: vehicles,
                baseCosts: uniqBy(vehicles, 'capacity').map((vehicle) => {
                  const existingBaseCost = values.baseCosts.find((baseCost) => baseCost.capacity === vehicle.capacity)
                  return {
                    amount: existingBaseCost?.amount || null,
                    capacity: vehicle.capacity
                  }
                })
              })
            }}
            partnerId={values.partner?.id}
            touched={getIn(touched, 'partnerVehicles')}
            errors={getIn(errors, 'partnerVehicles')}
            isShowingAllVehiclesOption={!isEmpty(values.partner?.id)}
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <RouteAutocomplete
            id="route"
            value={values.routes.at(0)?.name || ''}
            onChange={(routes): void => {
              setValues({
                ...values,
                routes,
                routeServices: []
              })
            }}
            placeholder={intl.formatMessage(messages.routePlaceholder)}
            label={<FormattedMessage id="partnerCost.form.route.label" defaultMessage="Route" />}
            verticals={PARTNER_COST_VERTICALS}
            uniqueName
            optional
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Label>
            <FormattedMessage id="partnerCost.form.services.label" defaultMessage="Services" />
          </Label>
          <MultiRouteServiceAutocomplete
            id="routeServices"
            values={values.routeServices}
            onChange={(routeServices): void => {
              setFieldValue('routeServices', routeServices)
            }}
            verticals={PARTNER_COST_VERTICALS}
            routeIds={values.routes.map(({ id }): string => id)}
            placeholder={intl.formatMessage(messages.routeServicesPlaceholder)}
            touched={touched.routeServices}
            errors={errors.routeServices}
            isShowingAllRouteServicesOption={!isEmpty(values.routes)}
            uniqueCode
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <InputDate
            intl={intl}
            id="startDate"
            value={values.startDate}
            onChange={(date): void => {
              const newDateRange = updateDateRangeByStartDate(
                {
                  startDate: values.startDate,
                  endDate: values.endDate
                },
                date as Date
              )
              setValues({
                ...values,
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
              })
            }}
            label={<FormattedMessage id="partnerCost.form.startDate.label" defaultMessage="Start Date" />}
            disabledDays={{
              before: subMonths(new Date(), 3)
            }}
            touched={getIn(touched, 'startDate')}
            errors={getIn(errors, 'startDate')}
          />
        </Column>
        <Column>
          <InputDate
            intl={intl}
            id="endDate"
            value={values.endDate || ''}
            onChange={(date): void => {
              const newDateRange = updateDateRangeByEndDate(
                {
                  startDate: values.startDate,
                  endDate: values.endDate
                },
                date as Date
              )
              setValues({
                ...values,
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
              })
            }}
            label={<FormattedMessage id="partnerCost.form.endDate.label" defaultMessage="End Date" />}
            placeholder={intl.formatMessage(messages.endAtPlaceholder)}
            disabledDays={{
              before: subMonths(new Date(), 3)
            }}
            touched={getIn(touched, 'endDate')}
            errors={getIn(errors, 'endDate')}
            optional
          />
        </Column>
        <Column>
          <DowSelector
            id="operationDays"
            values={values.operationDays}
            onChange={(operationDays: number[]): void => {
              setFieldValue('operationDays', operationDays)
            }}
            label={<FormattedMessage id="partnerCost.form.operationDays.label" defaultMessage="Operation Days" />}
            touched={getIn(touched, 'operationDays')}
            errors={getIn(errors, 'operationDays')}
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
